import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, merge, of } from 'rxjs';
import { first, mapTo, switchMap, tap } from 'rxjs/operators';
import {
   authenticationVerificationFailure,
   authenticationVerificationSuccess,
   unverifiedSessionFound,
} from 'src/app/app-start/app-start.actions';
import { UserSessionStorageService } from 'src/app/session/user-session-storage.service';
import { getIsLoggedIn } from '../state/authentication.selector';

@Injectable({
   providedIn: 'root',
})
export class AuthenticationGuard  {
   constructor(
      private router: Router,
      private store: Store,
      private actions$: Actions,
      private userSessionStorage: UserSessionStorageService
   ) {}

   canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
      return this.checkLogin(route, state);
   }

   canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
      return this.checkLogin(route, state);
   }

   private checkLogin(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
      return this.store.pipe(
         select(getIsLoggedIn),
         first(),
         switchMap((isLoggedIn) => {
            if (isLoggedIn) {
               return of(true);
            }

            const hasSession = this.userSessionStorage.getHasSession();

            if (!hasSession) {
               return this.redirectToLogin(state);
            }

            return this.verifyAuthentication(state);
         })
      );
   }

   private redirectToLogin(state: RouterStateSnapshot): Observable<boolean> {
      this.router.navigate(['/login'], { queryParams: { redirectUrl: state.url } });
      return of(false);
   }

   private verifyAuthentication(state: RouterStateSnapshot): Observable<boolean> {
      this.store.dispatch(unverifiedSessionFound());

      const success = this.actions$.pipe(ofType(authenticationVerificationSuccess), mapTo(true));

      const failure = this.actions$.pipe(
         ofType(authenticationVerificationFailure),
         tap(() => {
            return this.redirectToLogin(state);
         }),
         mapTo(false)
      );

      return merge(success, failure).pipe(first());
   }
}
